import { Button, Divider, Tooltip } from "@heroui/react";
import { Editor, EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Highlight from "@tiptap/extension-highlight";
import { useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Pilcrow,
  Redo,
  SeparatorHorizontal,
  Strikethrough,
  Undo,
} from "lucide-react";
import { t } from "@lingui/core/macro";

export type MarkdownEditorRef = {
  setContent: (content: string) => void;
};

export const MarkdownEditor = forwardRef<
  MarkdownEditorRef,
  {
    value?: string;
    onChange: (value: string) => void;
    dataIdentifier?: string;
  }
>(({ value, onChange, dataIdentifier }, ref) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {
          HTMLAttributes: {
            class: "list-disc ml-4",
          },
        },
        orderedList: {
          HTMLAttributes: {
            class: "list-decimal ml-4",
          },
        },
      }),
      Highlight,
    ],
    content: value ?? "",
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: "outline-none",
      },
    },
  });

  useImperativeHandle(ref, () => ({
    setContent: (content: string) => {
      editor?.commands.setContent(content ?? "");
    },
  }));

  useEffect(() => {
    if (value !== editor?.getHTML()) {
      editor?.commands.setContent(value ?? "");
    }
  }, [value, editor]);

  return (
    <div className="rounded-lg border-1 border-gray-200 p-2">
      <MenuBar editor={editor} />

      <Divider className="my-1" />

      <div className="p-1">
        <EditorContent editor={editor} data-identifier={dataIdentifier} />
      </div>
    </div>
  );
});

MarkdownEditor.displayName = "MarkdownEditor";

const MenuBar = ({ editor }: { editor: Editor }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-wrap gap-1">
      <ActionButton
        icon={<Bold size={16} />}
        onClick={() => editor.chain().focus().toggleBold().run()}
        tooltip={t`Krepko`}
      />
      <ActionButton
        icon={<Italic size={16} />}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        tooltip={t`Poševno`}
      />
      <ActionButton
        icon={<Strikethrough size={16} />}
        onClick={() => editor.chain().focus().toggleStrike().run()}
        tooltip={t`Prečrtano`}
      />
      <ActionButton
        icon={<List size={16} />}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        tooltip={t`Neurejen seznam`}
      />
      <ActionButton
        icon={<ListOrdered size={16} />}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        tooltip={t`Urejen seznam`}
      />
      <ActionButton
        icon={<SeparatorHorizontal size={16} />}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        tooltip={t`Vodoravna črta`}
      />
      <ActionButton
        icon={<Pilcrow size={16} />}
        onClick={() => editor.chain().focus().setHardBreak().run()}
        tooltip={t`Novi odstavek`}
      />
      <ActionButton
        icon={<Undo size={16} />}
        onClick={() => editor.chain().focus().undo().run()}
        tooltip={t`Razveljavi`}
      />
      <ActionButton
        icon={<Redo size={16} />}
        onClick={() => editor.chain().focus().redo().run()}
        tooltip={t`Ponovi`}
      />
    </div>
  );
};

const ActionButton = ({
  icon,
  onClick,
  tooltip,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  tooltip?: string;
}) => {
  return (
    <Tooltip
      content={tooltip}
      isDisabled={!tooltip}
      showArrow
      classNames={{
        base: "pointer-events-none",
      }}
    >
      <Button
        onPress={onClick}
        size="sm"
        isIconOnly
        variant="flat"
        type="button"
      >
        {icon}
      </Button>
    </Tooltip>
  );
};
